/**
 * QL-140 — the three client types the financial situation subsystem
 * distinguishes (fields, capacity variant, derived statuses, completion).
 */
export type FsClientType = "Person" | "Company" | "Group";

/** One classification band of a derived status (e.g. retail/0, affluent/200000). */
export type FinancialSituationStatusThreshold = {
  /** Status value assigned when the summed total reaches minValue (e.g. "affluent"). */
  value: string;
  /** Inclusive lower bound for this band. */
  minValue: number;
};

/**
 * Tenant-defined derived status on a financial situation model (QP-195).
 * Computed server-side on every save from the client's entered values and
 * stored in the instance's calculatedData — never written back to the client.
 */
export type FinancialSituationStatusDef = {
  /** Stable slug, e.g. "segment". */
  key: string;
  labelI18n: Record<string, string>;
  /**
   * Ids of the model's own fields to sum, per client type. QL-140 — `group` is
   * optional: models created before group support (and before the QL-140 data
   * migration) have only the two keys; readers treat a missing list as empty.
   */
  sourceFields: { person: string[]; company: string[]; group?: string[] };
  /**
   * QL-546 — per client type, also sum the client's synced account values
   * (the QL-40 snapshot, converted to the flow currency). Absent or false =
   * accounts do not count, which is how every status behaved before QL-546.
   */
  sourceAccounts?: { person?: boolean; company?: boolean; group?: boolean };
  thresholds: FinancialSituationStatusThreshold[];
};

/**
 * One field of a financial situation model.
 *
 * QL-39 — a field is a label plus flags: the former `type` taxonomy and the
 * help text are gone (nothing computed from them survived the QP-183 capacity
 * calculator, and designers never used the help text). Stored rows written
 * before QL-39 may still carry those keys; `sanitizeFieldDefinition` drops
 * them on every read and write.
 */
export type FinancialFieldDefinition = {
  id: string;
  /** Fallback display text — the label as last typed in the model editor. */
  label: string;
  /** Source of truth for display; readers fall back to `label` for locales missing here. */
  labelI18n?: Record<string, string>;
  /**
   * QL-39 — the advisor must enter a value for this field: the widget seeds a
   * row for it and marks it, and flow completion requires an entry with an
   * entered amount (QL-368: `0` counts). Absent = optional.
   */
  required?: boolean;
  hasMonthlyPayment?: boolean;
  /**
   * QL-330 — identifier an integration writes as `typeSyncKey` on an entry
   * instead of the generated `id`. Same rule as `wrapper_type.syncKey`:
   * trimmed, case-sensitive, exact match. Unique among the fields that apply
   * to one client type (the same key may sit on a Person, a Company and a
   * Group field). Absent = the field is addressed by `id` only.
   */
  syncKey?: string;
  /**
   * QP-176 — which client types this field applies to. Absent/empty means
   * Person-only (the pre-QP-176 behavior, when the whole component was
   * Person-only). QL-140 — a field may also be tagged "Group"; a field belongs
   * to exactly one type (no sharing between views).
   * Use `fieldAppliesTo` instead of reading this directly.
   */
  clientTypes?: FsClientType[];
};

/**
 * QP-176 — single source of truth for field applicability semantics.
 * Absent/empty `clientTypes` = Person-only, so models created before
 * company support behave exactly as they always have.
 */
export function fieldAppliesTo(
  field: Pick<FinancialFieldDefinition, "clientTypes">,
  clientType: FsClientType,
): boolean {
  if (!field.clientTypes || field.clientTypes.length === 0) {
    return clientType === "Person";
  }
  return field.clientTypes.includes(clientType);
}

/**
 * QL-140 — resolves a raw `client.type` for the FINANCIAL SITUATION path.
 * Unlike `suitabilityClientType` it keeps "Group" distinct, so a group sees
 * its own fields, capacity variant and status sources. Unknown/absent values
 * still fall back to "Person" — `client.type` is NOT NULL DEFAULT 'Person',
 * and Person is the codebase's conservative default.
 */
export function fsClientType(type?: string | null): FsClientType {
  if (type === "Company") return "Company";
  if (type === "Group") return "Group";
  return "Person";
}

/**
 * QP-176 — whether the household ratios (debt to income, monthly debt service
 * ratio, savings rate) apply to the client. They assume a personal household
 * (annual income), so a Company shows only its net worth. QL-140 — a Group of
 * natural persons is a household and keeps them. Shared by the advisor
 * widget's summary and the PDF Financial situation component (QL-384).
 */
export function fsShowsHouseholdRatios(clientType: FsClientType): boolean {
  return clientType !== "Company";
}

/**
 * QP-184 — maps a raw `client.type` to the Person|Company suitability union.
 * "Company" only when the type is exactly "Company"; "Group" and unknown
 * values behave as Person (a group of natural persons is suitability-assessed
 * like a household, and Person is the codebase's existing conservative
 * default).
 *
 * QL-140 — this collapse is now deliberate and narrow: it remains the resolver
 * for client CLASSIFICATION and AML/Trapets screening only. The financial
 * situation path uses `fsClientType`, which keeps "Group" distinct. Raw types
 * still flow to conditional display, custom-field filtering and the PDF
 * client_type placeholder. Pick between the two helpers deliberately.
 */
export function suitabilityClientType(type?: string | null): "Person" | "Company" {
  return type === "Company" ? "Company" : "Person";
}

/**
 * QL-127 — how the capacity triangle is entered. `monthly` (default): any two
 * of monthly income / expenses / surplus. `annual`: annual income and annual
 * expenses; the monthly surplus is derived and never typed.
 */
export const CAPACITY_INPUT_FREQUENCIES = ["monthly", "annual"] as const;
export type CapacityInputFrequency = (typeof CAPACITY_INPUT_FREQUENCIES)[number];

/** QL-127 — the monthly figure the buffer multiplier is applied to. */
export const CAPACITY_MULTIPLIER_BASES = ["income", "expenses"] as const;
export type CapacityMultiplierBasis = (typeof CAPACITY_MULTIPLIER_BASES)[number];

/** QP-183 — per-client-type investment capacity calculator config. */
export type InvestmentCapacityVariantConfig = {
  enabled: boolean;
  /** Absolute currency floor for the liquidity buffer minimum. */
  bufferFloor: number;
  /** Months of the multiplier basis (monthly income by default) the buffer minimum must cover. */
  bufferMultiplier: number;
  /** QL-127 — absent = `monthly`, exactly the pre-QL-127 behaviour. */
  inputFrequency?: CapacityInputFrequency;
  /** QL-127 — absent = `income`, exactly the pre-QL-127 behaviour. */
  bufferMultiplierBasis?: CapacityMultiplierBasis;
  shortTermExpensesEnabled?: boolean;
  /** Ids of the model's asset fields summed into the investable pool. */
  assetFieldIds: string[];
  /**
   * QL-107 — id of a tenant-defined boolean client field. When the client has
   * it ticked, `fixedBufferAmount` replaces the derived buffer minimum
   * outright (floor and multiplier stop participating). A chosen field IS the
   * enabled state; there is no separate flag. Absent/null = no fixed buffer.
   * The reference lives in settings, not a column, so nothing at the database
   * level clears it when the field is deleted — the rule simply stops applying.
   */
  fixedBufferClientField?: string | null;
  /** QL-107 — the fixed buffer amount, used literally (no per-member scaling). */
  fixedBufferAmount?: number | null;
};

/**
 * QP-176/QP-183 — business-rule settings on a financial situation model.
 * All flags default to off; an empty object is exactly the pre-existing
 * behavior. Read by the FS service (calculation), the flow-completion
 * engine (validation) and the FS widget (display).
 */
export type FinancialSituationModelSettings = {
  /** QP-176 — completion requires >=1 asset entry with amount > 0. */
  oneFieldInAssetsRequired?: boolean;
  /** QP-176 — completion requires >=1 liability entry with amount > 0. */
  oneFieldInLiabilitiesRequired?: boolean;
  /**
   * QP-176 — "are you expecting changes to your financial situation?"
   * When enabled the answer is required and a free-text note becomes
   * required when the answer is yes.
   */
  expectingChangesQuestion?: { enabled: boolean };
  /** QP-183 — investment capacity calculator + deposit suitability gate. */
  investmentCapacity?: {
    person?: InvestmentCapacityVariantConfig;
    company?: InvestmentCapacityVariantConfig;
    /** QL-140 — group clients; its buffer floor is an amount PER MEMBER. */
    group?: InvestmentCapacityVariantConfig;
    /**
     * Hard-block flow completion when planned deposits exceed capacity:
     * sum of one-time amounts vs available-for-investments, and monthly-
     * normalized recurring amounts vs monthly surplus. Custom portfolios count
     * the sum of their product rows (on an account follow-up: the buy deltas
     * against the existing holdings minus everything sold, floored at 0).
     */
    validateDeposits?: boolean;
  };
};

export type FinancialSituationModel = {
  id: string;
  name: string;
  description: string | null;
  incomeFields: string;
  assetFields: string;
  liabilityFields: string;
  commitmentFields: string;
  statuses: FinancialSituationStatusDef[];
  settings: FinancialSituationModelSettings;
  currency: string;
  language: string;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type FinancialSituationModelParsed = Omit<
  FinancialSituationModel,
  "incomeFields" | "assetFields" | "liabilityFields" | "commitmentFields"
> & {
  incomeFields: FinancialFieldDefinition[];
  assetFields: FinancialFieldDefinition[];
  liabilityFields: FinancialFieldDefinition[];
  commitmentFields: FinancialFieldDefinition[];
};
